/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import * as moment from 'moment';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject, Observable, of, combineLatest } from 'rxjs';
import {
	map,
	takeUntil,
	tap,
	endWith,
	filter,
	debounceTime,
	switchMap,
	distinctUntilChanged,
	delay,
} from 'rxjs/operators';
import { CoreState } from '../app-data/interfaces/app-state.interface';
import { Project } from '../app-data/models/Project';
import { ProjectsApi } from '../app-data/state/ProjectsApi';
import { PlansApi } from '../app-data/state/PlansApi';
import { ProjectStateLifeCycle, ProjectStateEvent } from '../app-data/models/ProjectStateLifeCycle';
import { FancyAction } from '../common/fancy-firebase/base/FancyAction';
import { DefaultActionTypes } from '../common/fancy-firebase/generics/DefaultActionTypes';
import { ProjectMembersApi, userProjectMemberObserver } from '../app-data/state/ProjectMembersApi';
import {
	mapValuesToArray,
	simpleSortArray,
	subjectSubscribeWithoutComplete,
} from '../plain-javascript/services/rxjs-internal-extensions';
import { sortProjectsByName } from '../utils/array-util';
import { LocalStorageHelper } from '../plain-javascript/services/LocalStorageHelper';
import { AppState } from './app-state.service';
import { RolesApi } from '../app-data/state/RolesApi';
import { LocationsApi } from '../app-data/state/LocationsApi';
import { InvitedUsersApi } from '../app-data/state/InvitedUsersApi';
import { ColorsApi } from '../app-data/state/ColorsApi';
import { analyticsEventsService } from 'src/app/plain-javascript/services/analytics/AnalyticsEventsService';
import { ProjectMember, Role, ProjectTypes } from '@touchplan/common';
import { ReportsUserPrefsService } from '../modules/reports/services/reports-user-prefs.service';
import { CustomFieldDefinitions } from '../app-data/state/CustomFieldDefinitions';
import { LoggerServiceFactory } from '../common/logging/LoggerServiceFactory';
import { ILoggerService } from '@touchplan/logging';
import { FeatureFlagManager } from './FeatureFlagManager';
import { DynamicFlagTypes } from '@touchplan/feature-flags';

export { ProjectStateEvent };

const lastSelectedProjectIdLocalStorageKey = 'lastDashboardSelectedProjectId';

/**
 * ProjectState is responsible for being the general repository for a "project derived"
 * state. Due to how projects work, there are two lifecycle "phases". Load the list of
 * projects, and select one specific project then load all of it's secondary data.
 */
@Injectable()
export class ProjectState implements CoreState {
	private logger: ILoggerService;
	private internalShutdown = new Subject<void>();
	private projectListShutdown = new Subject<void>();
	private stateLifeCycle = new ProjectStateLifeCycle();

	// lifecycle
	private stateChangeSubject$ = new BehaviorSubject(ProjectStateEvent.UNINITIALIZED);
	public stateChange$ = this.stateChangeSubject$
		.asObservable()
		.pipe(tap((state) => this.logger.log('selection in state', state)));
	public get stateSync() {
		return this.stateChangeSubject$.getValue();
	}

	/** special attributes which are above the "selected project" lifecycle */
	public projectsApi: ProjectsApi;
	private projectIdSubject$ = new BehaviorSubject<string>(null);
	public projectId$ = this.projectIdSubject$.asObservable();
	public get projectId() {
		return this.projectIdSubject$.getValue();
	}

	/** duplicate copy of the project list observable, but sorted */
	public sortedProjectList$ = this._sortedProjectsListGetter();

	//semi normal attributes
	private selectedProjectSubject$ = new BehaviorSubject<Project>(null);
	public selectedProject$ = this.selectedProjectSubject$.asObservable();
	public get selectedProjectSync() {
		return this.selectedProjectSubject$.getValue();
	}

	/** The projectMember corresponding to the logged in user and the selected project */
	public authedProjectMember$: Observable<ProjectMember>;
	public authedProjectMemberSync: ProjectMember = null;

	/**
	 * Special observable that emits a selected project but follows a slightly different logic set than the normal selected project flow.
	 * This one is intended to be used to drive how the dashboard gets populated. It works like:
	 * - if a project is actually selected, use that
	 * - if not, query localStorage for the last selected project
	 * - if that's undefined, use the first project
	 * - else null
	 */
	public selectedProjectIncludingPrevious$ = this._selectedProjectWithPrevious();

	// normal attributes
	public plansApi: PlansApi;
	public projectMembersApi: ProjectMembersApi;
	public rolesApi: RolesApi;
	public locationsApi: LocationsApi;
	public invitedUsersApi: InvitedUsersApi;
	public roleColorApi: ColorsApi;
	public locationColorApi: ColorsApi;
	public customFieldDefinitionsApi: CustomFieldDefinitions;

	//generally speaking the "state" services should not import things, but hierarchical imports of the other state services seems reasonable
	// plan can import project, project can import app, etc.
	constructor(
		private appState: AppState,
		private reportsUserPrefsService: ReportsUserPrefsService,
		private featureFlagManager: FeatureFlagManager
	) {
		this.logger = LoggerServiceFactory.create('ProjectStateService');
		this.monitorExpiredContractPatch();
	}

	/** manages the selectedProjectSubject$ property */
	private selectedProjectSetup(projectId: string) {
		const selectedProject$ = this.projectsApi.data.list$.pipe(
			map((list) => list.get(projectId)),
			takeUntil(this.internalShutdown)
		);
		// Generate Segment events when a project is selected:
		selectedProject$
			.pipe(
				filter((proj) => !!proj), // No undefined or null please
				distinctUntilChanged((proj1, proj2) => proj1.$id.localeCompare(proj2.$id) === 0), // No dupes either
				takeUntil(this.internalShutdown)
			)
			.subscribe((proj) => {
				analyticsEventsService.viewedAProject(proj.view.projectName);
			});

		selectedProject$
			.pipe(
				endWith(null as Project) //emit a null when this completes
			)
			.subscribe(subjectSubscribeWithoutComplete(this.selectedProjectSubject$));
		//it bugs me that this doesn't work...
		//.subscribe({ next: this.selectedProjectSubject$.next, error: this.selectedProjectSubject$.error }); //ignore completes
	}

	/**
	 * Loads the project globals, this sets up data that should be eagerly loaded _before_ selecting a project.
	 * Once again though
	 * you can assume that this method is called and thus data is loaded most of the time.
	 */
	public loadProjectGlobals(projectsApi: ProjectsApi) {
		this.projectsApi = projectsApi;
		this.stateChangeSubject$.next(this.stateLifeCycle.projectListLoaded());

		//drive the localStorage value for the last selected project
		this.selectedProject$.pipe(takeUntil(this.projectListShutdown)).subscribe((selectedProject) => {
			if (selectedProject) {
				LocalStorageHelper.write(lastSelectedProjectIdLocalStorageKey, selectedProject.$id);
			}
		});
	}

	/**
	 * Synonymous with selecting a project, this is the primary operating state of projectState.
	 * As in, you assume that this method has already been called most of the time. And thus all the data
	 * has been loaded in.
	 */
	public setup(
		projectId: string,
		plansApi: PlansApi,
		projectMembersApi: ProjectMembersApi,
		rolesApi: RolesApi,
		locationsApi: LocationsApi,
		invitedUsersApi: InvitedUsersApi,
		roleColorApi: ColorsApi,
		locationColorApi: ColorsApi,
		customFieldDefinitions: CustomFieldDefinitions
	) {
		if (!this.projectsApi) {
			throw new Error('projectState initialized in wrong order');
		}

		this.plansApi = plansApi;
		this.projectMembersApi = projectMembersApi;
		this.rolesApi = rolesApi;
		this.locationsApi = locationsApi;
		this.invitedUsersApi = invitedUsersApi;
		this.roleColorApi = roleColorApi;
		this.locationColorApi = locationColorApi;
		this.customFieldDefinitionsApi = customFieldDefinitions;

		this.projectIdSubject$.next(projectId);
		this.selectedProjectSetup(projectId);

		// Make sure we start loading the Reports UI prefs:
		this.reportsUserPrefsService.setIdsAndPreloadData(this.appState.userSync.$id, projectId);

		this.stateChangeSubject$.next(this.stateLifeCycle.projectSelected());

		//setup project member sync
		this.authedProjectMember$ = userProjectMemberObserver(
			this.appState.user,
			of(this.projectMembersApi)
		).pipe(
			takeUntil(this.internalShutdown),
			endWith(null as ProjectMember) //emit a null when this completes
		);
		this.authedProjectMember$.subscribe((sel) => (this.authedProjectMemberSync = sel));

		this.setupEventsForRoleAndMemberCreation();
	}

	/**
	 * Called when navigating to a route that does not have a corresponding project
	 */
	public boom() {
		if (this.stateSync < ProjectStateEvent.PROJECT_SELECTED) {
			return;
		}
		this.internalShutdown.next();
		this.projectIdSubject$.next(null);
		this.plansApi.destroy();
		this.plansApi = null;
		this.projectMembersApi.destroy();
		this.projectMembersApi = null;
		this.rolesApi.destroy();
		this.rolesApi = null;
		this.locationsApi.destroy();
		this.locationsApi = null;
		this.invitedUsersApi.destroy();
		this.invitedUsersApi = null;

		this.roleColorApi.destroy();
		this.roleColorApi = null;
		this.locationColorApi.destroy();
		this.locationColorApi = null;

		this.customFieldDefinitionsApi.destroy();
		this.customFieldDefinitionsApi = null;

		this.stateChangeSubject$.next(this.stateLifeCycle.boom());
	}

	/**
	 * Called when you don't want any project info at all, fairly uncommon, reset back to unitialized
	 */
	public destroyProjectsList() {
		if (this.stateSync === ProjectStateEvent.UNINITIALIZED) {
			return;
		}

		this.projectsApi.destroy();

		this.projectsApi = null;
		this.stateChangeSubject$.next(this.stateLifeCycle.reset());
		this.projectListShutdown.next();
	}

	/** Creates a stable "always accessible" list of sorted projects that lives outside of the lifecycle */
	private _sortedProjectsListGetter(): Observable<Project[]> {
		return this.stateChangeSubject$.pipe(
			switchMap((state) => {
				if (state >= ProjectStateEvent.PROJECTS_INITIALIZED) {
					return this.projectsApi.data.list$.pipe(
						debounceTime(10),
						mapValuesToArray(),
						simpleSortArray(sortProjectsByName)
					);
				} else {
					return of([]);
				}
			})
		);
	}

	/**
	 * Create a special "dashboard safe" version of the selectedProject
	 * logic is something like:
	 * - is a project actually selected, use that
	 * - else pull the last from localStorage
	 * - else use the first in the list
	 */
	private _selectedProjectWithPrevious() {
		return combineLatest([this.selectedProject$, this.sortedProjectList$]).pipe(
			map((combined) => {
				const selectedProject = combined[0];
				const sortedProjects = combined[1];
				const localStorageProjectId = LocalStorageHelper.read(lastSelectedProjectIdLocalStorageKey);

				if (selectedProject) {
					return selectedProject;
				}
				if (localStorageProjectId) {
					const proj = sortedProjects.find((p) => p.$id === localStorageProjectId);
					if (proj) {
						return proj;
					}
				}
				if (sortedProjects.length > 0) {
					return sortedProjects[0];
				}

				return null;
			})
		);
	}

	// short term hack for https://mocasystems.atlassian.net/browse/TP-1659
	// lives for duration of app
	// has the fairly unique goal of semantically matching the validateProjectType function
	private monitorExpiredContractPatch() {
		combineLatest([
			this.selectedProject$,
			this.featureFlagManager.getFlag$(DynamicFlagTypes.MARK_PROJECTS_AS_ARCHIVED_IN_DESKTOP),
		])
			.pipe(
				filter(
					([project, markProjectsAsArchived]) =>
						markProjectsAsArchived && project && project.view.projectType !== ProjectTypes.DISABLED
				)
			)
			.subscribe(([project, markProjectsAsArchived]) => {
				let typeToWrite: ProjectTypes;
				//mimic silly old logic for "safety"
				if (project.view.invoiceEndDate) {
					const expired = moment().isAfter(moment(project.view.invoiceEndDate), 'days');
					if (project.view.projectType === ProjectTypes.INVOCIE && expired) {
						typeToWrite = ProjectTypes.INVOICE_EXPIRED;
					} else if (
						project.view.projectType !== ProjectTypes.INVOCIE &&
						project.view.projectType !== ProjectTypes.INVOICE_EXPIRED
					) {
						typeToWrite = ProjectTypes.INVOCIE;
					}
				} else if (project.view.projectType === ProjectTypes.TRIALING) {
					const expired = moment().isAfter(moment(project.view.freeTrialStamp), 'days');
					if (expired) {
						typeToWrite = ProjectTypes.TRIAL_EXPIRED;
					}
				}

				if (typeToWrite) {
					this.projectsApi.localUpdate(project.$id, { projectType: typeToWrite });
					this.projectsApi.persistData(project);
				}
			});
	}

	private setupEventsForRoleAndMemberCreation() {
		this.projectIdSubject$
			.pipe(
				switchMap((__) => this.rolesApi.data.rawEvent$),
				filter((event) => event.type === DefaultActionTypes.addItem),
				takeUntil(this.internalShutdown)
			)
			.subscribe((event) => this.analyticsForRoleAddition(event));

		this.projectMembersApi.data.rawEvent$
			.pipe(
				filter((event) => event.type === DefaultActionTypes.addItem),
				// Delay (hack) to let other subscribers handle the add first.
				// There's no rush to log the segment events.
				// This gives time for the join completes (if necessary) which in
				// turn makes sure the "...user.email" access doesn't fail on
				// a "null" access.  (TP-1482)
				delay(500),
				takeUntil(this.internalShutdown)
			)
			.subscribe((event) => this.analyticsForProjectMemberAddition(event));
	}

	private analyticsForRoleAddition(event: FancyAction<Role>) {
		const role = event.payload;
		analyticsEventsService.createdARole(role.$id, role.view.roleName);
	}

	private analyticsForProjectMemberAddition(event: FancyAction<ProjectMember>) {
		const mem = event.payload;
		if (!!mem?.view?.user?.email) {
			analyticsEventsService.addProjectMemberUserExists(mem.$id, mem.view.user.email);
		}
	}
}
